import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setIsAuthenticated,
  setUserData,
  clearAuth,
  setInitializing,
} from "../Redux/Slices/Pages/Auth/LoginSlice";
import {
  getRefreshToken,
  getAccessToken,
  storeTokens,
  isLocalToRemote,
} from "../utils/authHelper";
import { plainAxios } from "../Common/Interceptors/AxiosConfig";
import { getUseFromStorage, getAuthStatusFromStorage, setServiceProviderEnabled } from "../utils/storage";

// ✅ isLocalToRemote() handles all cases:
// bookizap.com web   → false → cookie mode
// bookizap.com TWA   → true  → token mode
// testing            → true  → token mode
// localhost          → true  → token mode
const isTokenAuthMode = () => isLocalToRemote();

const PUBLIC_EXACT_ROUTES = new Set([
  "/", "/login", "/register", "/forgot-password", "/otp",
  "/privacy-policy", "/terms-conditions", "/contact",
  "/refund-cancellation", "/ourServices", "/aboutUs",
]);

const PUBLIC_PREFIX_ROUTES = [
  "/service-details/",
  "/services/service-group/",
  "/service-invitation/",
  "/myBooking/accepted",   // ✅ WhatsApp magic-link destination for clients
  "/serviceProvider/access/",  // ✅ WhatsApp magic-link destination for providers
  "/serviceProvider/payments", // ✅ WhatsApp magic-link destination for providers
  // ✅ Discovery funnel is fully public — no auth required to browse
  "/master-service/",
  "/explore",
  "/profile/",   // ✅ Public workspace profile pages — shareable without login
  "/s/",         // ✅ Legacy short-link support
  "/w/",         // ✅ Industry-standard workspace public page (/w/:workspaceSlug)
  "/zap/watch/", // ✅ Immersive post feed - shareable without login
  "/workspaces/", // ✅ Public workspace post links
];

/**
 * ✅ Compute isPublicRoute at call-time using window.location.pathname
 * This avoids stale closures when the effect runs only once on mount.
 */
const checkIsPublicRoute = () => {
  const currentPath = window.location.pathname;
  const isDynamic = /^\/[a-f0-9]{24}\/master-services/.test(currentPath);
  return (
    PUBLIC_EXACT_ROUTES.has(currentPath) ||
    PUBLIC_PREFIX_ROUTES.some((p) => currentPath.startsWith(p)) ||
    isDynamic
  );
};

function AuthInitializer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.login.isAuthenticated);
  const authInitialized = useRef(false);

  const getRefreshUrl = () => {
    if (window.location.hostname.includes("admin")) {
      const isSuperAdmin = localStorage.getItem('superadmin_isAuthenticated') || localStorage.getItem('superadmin_userRole') === 'superadmin';
      return isSuperAdmin ? "/super-admin/auth/refresh" : "/admin/auth/refresh";
    }
    return "/users/auth/refresh";
  };

  // ── Silent refresh on page load — runs ONCE on mount ──────────────────────
  // ✅ FIX: Removed location.pathname from deps to prevent re-running on every
  // navigation. In token mode, re-running consumed the one-time-use refresh
  // token via a concurrent call with AxiosConfig's interceptor, burning the
  // session. In cookie mode, it caused unnecessary refresh API calls.
  // Uses a ref guard to guarantee single execution even in React StrictMode.
  useEffect(() => {
    if (authInitialized.current) return;
    authInitialized.current = true;
    console.log("🔐 [AuthInitializer] Starting session validation...");

    let isMounted = true;
    const initializeAuth = async () => {
      // ✅ Read current pathname at call-time, not from stale closure
      const isPublic = checkIsPublicRoute();
      const storedUser = getUseFromStorage();

      if (!isTokenAuthMode()) {
        // ── Cookie mode → bookizap.com WEB BROWSER only ──────────────────────
        const authStatus = getAuthStatusFromStorage();

        if (storedUser && authStatus) {
          // ✅ Zero-latency restoration: skip initializing state
          dispatch(setUserData(storedUser));
          dispatch(setIsAuthenticated(true));
          dispatch(setInitializing(false));
          return;
        }

        try {
          const response = await plainAxios.post(getRefreshUrl(), {});
          if (!isMounted) return;

          if (response?.data?.status === "success") {
            const user = response.data?.data?.user;
            if (user) dispatch(setUserData(user));
            else if (storedUser) dispatch(setUserData(storedUser));
            dispatch(setIsAuthenticated(true));
            localStorage.setItem('isAuthenticated', 'true');

            const bodyAccessToken = response.data?.data?.accessToken;
            if (bodyAccessToken) storeTokens(bodyAccessToken, null);

            if (response.data.data?.isServiceProviderEnabled !== undefined) {
              setServiceProviderEnabled(response.data.data.isServiceProviderEnabled);
            }
          } else {
            if (!isPublic) navigate("/login");
          }
        } catch (error) {
          if (!isMounted) return;
          // ✅ FIX: Only clear auth if the server explicitly rejects the token (401/403/400/404)
          // If it's a network error (no response) or rate limit (429), don't immediately kick the user out.
          if (error.response) {
            const status = error.response.status;
            if (status === 401 || status === 403 || status === 400 || status === 404) {
              dispatch(clearAuth());
              if (!isPublic) navigate("/login");
            }
          } else {
            // Keep current local state, let axiosInstance interceptor retry background calls
          }
        }
        dispatch(setInitializing(false));
        return;
      }

      // ── Token mode → localhost + testing + TWA ────────────────────────────
      const refreshToken = getRefreshToken();
      const accessToken = getAccessToken();

      if (!refreshToken) {
        console.warn("⚠️ [AuthInitializer] No refresh token found.");
        // ✅ FIX: Do NOT aggressively clearAuth() here.
        // If they have an accessToken, let them use it. Axios will kick them out if it fails.
        // If they have NOTHING, then we can assume they are logged out.
        if (!accessToken) {
          dispatch(clearAuth());
          if (!isPublic) navigate("/login");
          dispatch(setInitializing(false));
          return;
        }
      }

      // ✅ FIX: If we already have an access token, use it! Let Axios interceptor handle 401s.
      // Force-refreshing here causes a race condition with Axios interceptor on page load,
      // leading to token reuse detection and session revocation.
      if (accessToken) {
        console.log("✅ [AuthInitializer] Restoring session from memory tokens...");
        if (storedUser) dispatch(setUserData(storedUser));
        dispatch(setIsAuthenticated(true));
        dispatch(setInitializing(false)); // Ensure we don't block
        return;
      }

      // Only set initializing if we actually need to do a network refresh
      if (!accessToken) {
        dispatch(setInitializing(true));
      }

      try {
        const response = await plainAxios.post(
          getRefreshUrl(),
          { refreshToken },
          { headers: { "X-Token-Delivery": "body" } }
        );
        if (!isMounted) return;

        if (response?.data?.status === "success") {
          const { accessToken: newAT, refreshToken: newRT } = response.data.data.tokens || response.data.data;
          storeTokens(newAT, newRT);
          if (storedUser) dispatch(setUserData(storedUser));
          dispatch(setIsAuthenticated(true));

          if (response.data.data?.isServiceProviderEnabled !== undefined) {
            setServiceProviderEnabled(response.data.data.isServiceProviderEnabled);
          }
        } else {
           throw new Error("Refresh failed");
        }
      } catch (err) {
        if (!isMounted) return;

        // ✅ Only clear auth if the server explicitly rejects the token (401/403/400/404)
        // If it's a network error (ERR_FAILED) or rate limit (429), don't immediately kick the user out.
        if (err.response) {
          const status = err.response.status;
          if (status === 401 || status === 403 || status === 400 || status === 404) {
            dispatch(clearAuth());
            if (!isPublic) navigate("/login");
          }
        } else {

          // We keep the local isAuthenticated state for now, axiosInstance will retry later
        }
      } finally {
        if (isMounted) dispatch(setInitializing(false));
      }
    };

    initializeAuth();
    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Listen for auth failure events from AxiosConfig (SPA-safe redirect) ───
  // ✅ FIX: AxiosConfig now dispatches BZ_AUTH_FAILURE instead of using
  // window.location.href which caused full page reloads and re-triggered
  // initializeAuth, creating the infinite refresh loop.
  // This works identically for both cookie mode and token mode.
  const authFailureHandled = useRef(false);
  useEffect(() => {
    const handleAuthFailure = () => {
      // ✅ Guard: Only process the first auth failure event.
      // Multiple 401s from concurrent API calls each fire this event.
      // Without this guard, repeated clearAuth() + navigate() causes flicker.
      if (authFailureHandled.current) return;
      authFailureHandled.current = true;

      dispatch(clearAuth());
      const isPublic = checkIsPublicRoute();
      if (!isPublic) {
        navigate("/login");
      }

      // Reset after a settle period so future auth failures (e.g., after re-login)
      // can still be handled.
      setTimeout(() => { authFailureHandled.current = false; }, 2000);
    };

    window.addEventListener('BZ_AUTH_FAILURE', handleAuthFailure);
    return () => window.removeEventListener('BZ_AUTH_FAILURE', handleAuthFailure);
  }, [dispatch, navigate]);

  // ── Notifications + deep links ────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    // dispatch(fetchNotifications());
    const pendingContext = localStorage.getItem("pendingNotificationContext");
    if (pendingContext) {
      try {
        const parsed = JSON.parse(pendingContext);
        localStorage.removeItem("pendingNotificationContext");
        navigate("/notification", {
          state: {
            focusNotificationId: parsed.notificationId,
            invitationToken: parsed.invitationToken,
          },
        });
      } catch {
        localStorage.removeItem("pendingNotificationContext");
      }
    }
  }, [isAuthenticated, dispatch, navigate]);

  return null;
}

export default AuthInitializer;
